import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MAINTENANCE_CATEGORIES, MAINTENANCE_PRIORITIES, MAINTENANCE_STATUSES } from "@rentorng/core";
import { api } from "@/lib/api";
import { ExportButton } from "@/components/export-button";
import { Pagination, SearchBox } from "@/components/list-toolbar";
import { useListParams, type PageMeta } from "@/lib/list-params";
import { titleCase, useFormat } from "@/lib/format";
import { useApiMutation } from "@/lib/mutations";
import { resolveUnit, searchUnits } from "@/lib/pickers";
import { SearchSelect } from "@/components/search-select";
import { useSession } from "@/lib/session";
import { MaintenanceStatusBadge, PriorityBadge } from "@/components/status";
import { Alert, Badge, Button, Card, CardTitle, EmptyState, ErrorState, Field, Input, PageHeader, Select, Spinner, Table, Textarea } from "@/components/ui";
import { MoneyInput } from "@/components/money-input";

/**
 * Maintenance.
 *
 * The SLA clock is the only thing on this screen that is not obvious: each priority carries a
 * response target, and a job past it reaches the dashboard action queue. That is what turns a
 * ticket list into something a manager is actually held to.
 */

interface Row {
  id: string;
  reference: string;
  title: string;
  category: string | null;
  priority: string;
  status: string;
  slaDueAt: string | null;
  createdAt: string;
  estimatedCost: number | null;
  actualCost: number | null;
  unitLabel: string | null;
  propertyName: string | null;
  assigneeName: string | null;
}

export function MaintenancePage() {
  const { vocab, can } = useSession();
  const fmt = useFormat();
  /*
    The filter lives in the address, not in component state.

    It was state, which meant `/app/maintenance?open=1` — the link the dashboard's open-jobs figure
    needs — was silently ignored and landed on every job. The rule across every other list here is
    that the URL is the state: it survives a refresh, a back button, and being pasted to a
    colleague, and it is the only way a figure elsewhere can link to the list that matches it.
  */
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const open = params.get("open") === "1";
  const setFilter = (next: { status?: string; open?: boolean }) =>
    setParams(
      (current) => {
        const query = new URLSearchParams(current);
        if (next.status) query.set("status", next.status);
        else query.delete("status");
        if (next.open) query.set("open", "1");
        else query.delete("open");
        query.delete("page"); // a new filter starts at the beginning
        return query;
      },
      { replace: true },
    );

  const [adding, setAdding] = useState(false);
  const [unitId, setUnitId] = useState("");

  const list = useListParams();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["maintenance", status, open, list.q, list.page],
    queryFn: () =>
      api.get<{ requests: Row[]; now: string; page: PageMeta }>("/maintenance", {
        status,
        open: open ? "1" : "",
        q: list.q,
        page: list.page,
      }),
    placeholderData: (previous) => previous,
  });

  const create = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post("/maintenance", input),
    invalidates: [["maintenance"], ["dashboard"], ["nav-counts"]],
    onSuccess: () => setAdding(false),
  });

  const update = useApiMutation({
    mutationFn: ({ id, ...input }: { id: string } & Record<string, unknown>) => api.patch(`/maintenance/${id}`, input),
    invalidates: [["maintenance"], ["dashboard"], ["nav-counts"]],
  });

  const rows = data?.requests ?? [];
  const now = data?.now ? new Date(data.now).getTime() : Date.now();
  const breached = rows.filter((row) => row.slaDueAt && new Date(row.slaDueAt).getTime() < now).length;

  return (
    <div>
      <PageHeader
        title="Maintenance"
        subtitle={`${rows.length} shown${breached ? ` · ${breached} past their response target` : ""}`}
        description={"Jobs reported and jobs done, each with a response target. Assign somebody, record what it cost, and recharge it to the tenant where the damage was theirs."}
        actions={
          <>
            <ExportButton kind="maintenance" />
            {can("maintenance.edit") ? <Button onClick={() => setAdding((v) => !v)}>{adding ? "Close" : "Log a job"}</Button> : null}
          </>
        }
      />

      {adding ? (
        <Card className="mb-5">
          <CardTitle hint="The response target is set from the priority — emergency 4 hours, high 24, normal 72, low a week.">
            New maintenance job
          </CardTitle>
          {create.errorMessage ? <Alert tone="danger">{create.errorMessage}</Alert> : null}
          <form
            className="grid gap-4 sm:grid-cols-3"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              create.mutate({
                title: String(form.get("title")),
                description: String(form.get("description") || "") || null,
                unitId: String(form.get("unitId") || "") || null,
                category: String(form.get("category") || "") || null,
                priority: String(form.get("priority")),
                estimatedCost: String(form.get("estimatedCost") || "") || null,
                rechargeToTenant: form.get("rechargeToTenant") === "on",
              });
            }}
          >
            <Field label="What is wrong" htmlFor="title" required>
              <Input id="title" name="title" required placeholder="Leaking kitchen tap" />
            </Field>
            <Field label={titleCase(vocab.unit)} htmlFor="unitId">
              <SearchSelect
                id="unitId"
                name="unitId"
                value={unitId}
                onChange={(next) => setUnitId(next)}
                search={searchUnits}
                resolve={resolveUnit}
                placeholder="Not linked"
                emptyMessage={`No ${vocab.unit} matched.`}
              />
            </Field>
            <Field label="Category" htmlFor="category">
              <Select id="category" name="category">
                {MAINTENANCE_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Priority" htmlFor="priority">
              <Select id="priority" name="priority" defaultValue="normal">
                {MAINTENANCE_PRIORITIES.map((priority) => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label} · {priority.slaHours}h
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Estimated cost" htmlFor="estimatedCost">
              <MoneyInput id="estimatedCost" name="estimatedCost" />
            </Field>
            <label className="flex items-center gap-2 self-end text-sm">
              <input type="checkbox" name="rechargeToTenant" className="size-4 rounded border-ink-300" />
              Recharge to the {vocab.tenant}
            </label>
            <Field label="Detail" htmlFor="description">
              <Textarea id="description" name="description" rows={2} placeholder="What is wrong, and where." />
            </Field>
            <div className="sm:col-span-3">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Logging…" : "Log the job"}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <Button variant={!status && !open ? "primary" : "secondary"} size="sm" onClick={() => setFilter({})}>
          All
        </Button>
        {/* "Still open" is one filter across four statuses, and it is what the dashboard counts.
            Without it here, the figure on the dashboard had no list that agreed with it. */}
        <Button variant={open ? "primary" : "secondary"} size="sm" onClick={() => setFilter({ open: true })}>
          Still open
        </Button>
        {MAINTENANCE_STATUSES.map((option) => (
          <Button
            key={option.value}
            variant={status === option.value ? "primary" : "secondary"}
            size="sm"
            onClick={() => setFilter({ status: option.value })}
          >
            {option.label}
          </Button>
        ))}
      </div>

      <Card padded={false}>
        <div className="border-b border-ink-200 p-3">
          <SearchBox params={list} placeholder={`Search a job, reference or ${vocab.property}…`} />
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={() => refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState title="No maintenance jobs here." hint="Jobs raised by staff, by a tenant, or from an inspection all land here." />
        ) : (
          <Table
            head={
              <tr>
                <th>Reference</th>
                <th>Job</th>
                <th>Where</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Target</th>
                <th className="num">Cost</th>
                <th></th>
              </tr>
            }
          >
            {rows.map((row) => {
              const late = row.slaDueAt && new Date(row.slaDueAt).getTime() < now;
              return (
                <tr key={row.id}>
                  <td className="text-ink-500">{row.reference}</td>
                  <td>
                    <span className="font-medium">{row.title}</span>
                    {row.category ? <span className="block text-xs text-ink-400">{row.category}</span> : null}
                  </td>
                  <td className="text-ink-500">
                    {row.unitLabel ?? "—"}
                    {row.propertyName ? <span className="block text-xs text-ink-400">{row.propertyName}</span> : null}
                  </td>
                  <td>
                    <PriorityBadge priority={row.priority} />
                  </td>
                  <td>
                    <MaintenanceStatusBadge status={row.status} />
                  </td>
                  <td className="text-xs">
                    {row.slaDueAt ? (
                      late ? (
                        <Badge tone="danger" dot>
                          Overdue
                        </Badge>
                      ) : (
                        <span className="text-ink-500">{fmt.dateTime(row.slaDueAt)}</span>
                      )
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="num tabular">{fmt.money(row.actualCost ?? row.estimatedCost)}</td>
                  <td>
                    {can("maintenance.edit") ? (
                      <Select
                        aria-label={`Change status of ${row.reference}`}
                        value={row.status}
                        onChange={(event) => update.mutate({ id: row.id, status: event.target.value })}
                        className="w-36 text-xs"
                      >
                        {MAINTENANCE_STATUSES.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </Table>
        )}

        <Pagination meta={data?.page} params={list} noun={"jobs"} />
      </Card>
    </div>
  );
}
